import { Component, ReadOnlyRef, Ref, useContext, useEffect, useRef } from "@lukekaalim/act";
import { TheatreState, TheatreStateContext } from "./useTheatreState";
import { Graphics3DSetup, graphicsSetupContext, inputBeat, renderBeat } from "@kosmod/graphics-3d";
import { createJanitor, ID, subscribeEvent, useBeat, useJanitorEffect, useJeffect, useLogger } from "@kosmod/kit";
import * as three from 'three';
import { Surface, SurfaceID, TheatrePosition } from "../defs";
import { a3 } from "@lukekaalim/act-three";
import { TheatreController } from "./useTheatreController";

// const DEFAULT_INTERSECT = (ray: three.Ray, cursorPos: three.Vector2) => {
//   const intersection = ray.intersectPlane(new three.Plane(new three.Vector3(0, 1, 0), 0), new three.Vector3)
//   if (intersection) {
//     cursorPos.set(intersection.x, intersection.z);
//   } else {

//   }
// }

// export const useRaycastCursorPosition = (
//   state: TheatreState,
//   intersect: (ray: three.Ray, cursor_pos: three.Vector2) => void = DEFAULT_INTERSECT,
//   graphicsOverride: null | Graphics3DSetup = null
// ) => {
//   const graphicsContext = useContext(graphicsSetupContext);
//   const onInput = useBeat(inputBeat);

//   const graphics = graphicsOverride || graphicsContext;

//   useEffect(() => {
//     const container = state.graphics.canvas.get();
//     const camera = graphics.camera.get();
    
//     if (!container || !camera)
//       return;

//     const cursor_pos = new three.Vector2;
//     const mouse_pos = new three.Vector2;
//     const raycaster = new three.Raycaster();

//     const onMouseMove = (event: MouseEvent) => {
//       state.cursorPosition.set(cursor_pos);
//       mouse_pos.x = (((event.clientX * 2) - container.clientWidth) / container.clientWidth);
//       mouse_pos.y = -((event.clientY * 2) - container.clientHeight) / container.clientHeight;
//     };
//     const onPointerEnter = () => {
//       state.cursorPosition.set(cursor_pos);
//     }
    
//     const onPointerExit = () => {
//       state.cursorPosition.set(null);
//     }

//     onInput(() => {
//       raycaster.setFromCamera(mouse_pos, camera);
//       intersect(raycaster.ray, cursor_pos);
//     })

//     container.addEventListener('pointermove', onMouseMove);
//     container.addEventListener('pointerenter', onPointerEnter);
//     container.addEventListener('pointerleave', onPointerExit);
//     return () => {
//       container.removeEventListener('pointermove', onMouseMove);
//       container.removeEventListener('pointerenter', onPointerEnter);
//       container.removeEventListener('pointerleave', onPointerExit);
//     }
//   }, [state, graphics, intersect])

// };

// export const DEFAULT_SURFACE = 'DEFAULT_SURFACE' as SurfaceID

/**
 * Maintains a raycaster each animFrame which
 * points to the current TheatrePosition of the cursor.
 * 
 * A "null" value means there is no cursor - if a
 * cursor "misses" all floors on the scene, it automatically
 * hits the default floor
 */
export const useTheatreCursor = (
  cameraRef: ReadOnlyRef<null | three.PerspectiveCamera>,
  canvasRef: ReadOnlyRef<null | HTMLCanvasElement>,

  containerRef: ReadOnlyRef<null | HTMLElement>,
  container2DRef: ReadOnlyRef<null | HTMLElement>,

  controller: TheatreController,
  defaultSurfaceId: null | SurfaceID,
  surfaces: Surface[],
  enabled3D: boolean = false,
) => {
  const cursorRef = useRef<null | TheatrePosition>(null);
  const cursor2DRef = useRef<null | three.Vector2>(null);

  const logger = useLogger(`TheatreCursor`);

  const onInput = inputBeat.useBeat();

  const cursorState = useRef(() => {

  })

  useJeffect((j) => {
    const camera = cameraRef.get();
    const canvas = canvasRef.get();
    const container = containerRef.get();
    const container2D = container2DRef.get();

    if (!camera || !canvas || !container || !container2D) {
      cursorRef.set(null);
      cursor2DRef.set(null);
      return;
    }
    const DEFAULT_SURFACE = surfaces.find(s => s.id === defaultSurfaceId);
    if (!DEFAULT_SURFACE)
      return;

    const cursor_pos: TheatrePosition = { type: 'grid', surfaceId: DEFAULT_SURFACE!.id, vector: { x: 0, y: 0 } }
    const cursor2D = new three.Vector2(0, 0);

    const intersection = new three.Vector3();
    const mouse_pos = new three.Vector2();
    const raycaster = new three.Raycaster();

    const onPointerMove = (event: PointerEvent | DragEvent) => {
      const rect = canvas.getBoundingClientRect()

      if (event.currentTarget === container2D) {
        cursor2D.x = event.clientX - rect.x;
        cursor2D.y = event.clientY - rect.y;
        return;
      }

      cursorRef.set(cursor_pos);

      mouse_pos.x = ((((event.clientX - rect.x) * 2) - rect.width) / rect.width);
      mouse_pos.y = -(((event.clientY - rect.y) * 2) - rect.height) / rect.height;
    };
    const onCanvasPointerEnter = () => {
      cursorRef.set(cursor_pos);
    }

    let hasPointer = false;
    let hasDrag = false;
    
    const onCanvasPointerExit = () => {
      cursorRef.set(null);
    }

    const setCursorValue = (e: MouseEvent) => {
      if (hasPointer || hasDrag) {
        cursor2DRef.set(cursor2D);
      } else {
        cursor2DRef.set(null)
      }
    }
    const onPointerEnter = (e: MouseEvent) => {
      if (e.target !== e.currentTarget)
        return;

      hasPointer = true;
      setCursorValue(e);
    }
    
    const onPointerExit = (e: MouseEvent) => {
      if (e.target !== e.currentTarget)
        return;

      hasPointer = false;
      setCursorValue(e);
    }
    const onDragStart = (e: MouseEvent) => {
      hasDrag = true;
      setCursorValue(e);
    };
    const onDragEnd = (e: MouseEvent) => {
      hasDrag = false;
      setCursorValue(e);
    }

    const DEFAULT_PLANE = new three.Plane(new three.Vector3(0, 1, 0), 0);


    j.add(subscribeEvent(container2D, 'dragstart', onDragStart));
    j.add(subscribeEvent(container2D, 'dragover', onPointerMove));
    j.add(subscribeEvent(container2D, 'dragend', onDragEnd));

    j.add(subscribeEvent(container, 'pointermove', onPointerMove));
    j.add(subscribeEvent(container2D, 'pointermove', onPointerMove));

    j.add(subscribeEvent(container2D, 'pointerenter', onPointerEnter));
    j.add(subscribeEvent(container2D, 'pointerleave', onPointerExit));

    if (enabled3D) {
      j.add(subscribeEvent(canvas, 'pointerenter', onCanvasPointerEnter));
      j.add(subscribeEvent(canvas, 'pointerleave', onCanvasPointerExit));
      
      j.add(onInput(() => {
        raycaster.setFromCamera(mouse_pos, camera);
        const result = raycaster.ray.intersectPlane(DEFAULT_PLANE, intersection)

        if (result) {
          cursor_pos.vector.x = result.x;
          cursor_pos.vector.y = result.z;
        }
      }))
    }
    
  }, [onInput, enabled3D])

  return [cursorRef, cursor2DRef] as const;
}

